import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Users, Loader2, RefreshCw, Zap, ShieldAlert } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const HOUSE_KEYS = ['lions', 'dragons', 'eagles', 'wolves'];

const StatCard = ({ label, value, testid, tone = 'default' }) => (
  <div className={`rounded-xl border p-4 ${
    tone === 'gold' ? 'bg-gradient-to-br from-amber-50 to-yellow-100 border-amber-300'
      : 'bg-white border-gray-200'
  }`}>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-2xl font-black text-black mt-1" data-testid={testid}>{value}</div>
  </div>
);

export default function AdminEconomyPanel() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/admin/economy/overview');
      setOverview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load overview');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/economy/backfill');
      toast.success(`Backfill complete — created ${r.data.created}, already OK ${r.data.already_provisioned}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally { setBusy(false); }
  };

  const grantCoins = async () => {
    const email = prompt('Student email to grant coins to?');
    if (!email) return;
    const amountStr = prompt('How many WinCoins?');
    const amount = parseInt(amountStr, 10);
    if (!amount || amount <= 0) return;
    const reason = prompt('Reason (short label)?', 'admin_grant') || 'admin_grant';
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/economy/grant-coins', { email, amount, reason });
      toast.success(`Granted ${r.data.awarded} coins. New balance: ${r.data.balance}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Grant failed');
    } finally { setBusy(false); }
  };

  const setHouse = async () => {
    const email = prompt('Student email?');
    if (!email) return;
    const house = prompt(`House? (${HOUSE_KEYS.join(', ')})`);
    if (!HOUSE_KEYS.includes(house)) { toast.error('Invalid house'); return; }
    setBusy(true);
    try {
      await ax.post('/api/admin/economy/houses/set', { email, house });
      toast.success(`${email} moved to ${house}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Set failed');
    } finally { setBusy(false); }
  };

  if (loading || !overview) {
    return <div className="p-8 text-center" data-testid="admin-economy-loading"><Loader2 className="h-6 w-6 animate-spin text-red-700 mx-auto" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-economy-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-700">Phase 11 · Winhouse Economy</div>
          <h3 className="text-lg font-bold flex items-center gap-2"><Coins className="h-5 w-5 text-amber-500" /> Economy Overview</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={load} disabled={busy}
            className="h-9 px-3 rounded-lg bg-gray-100 hover:bg-gray-200 text-black text-xs font-bold inline-flex items-center gap-1"
            data-testid="admin-economy-reload">
            <RefreshCw className="h-3.5 w-3.5" /> Reload
          </button>
          <button onClick={runBackfill} disabled={busy}
            className="h-9 px-3 rounded-lg bg-red-700 hover:bg-red-800 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-economy-backfill">
            <Zap className="h-3.5 w-3.5" /> Run Backfill
          </button>
          <button onClick={grantCoins} disabled={busy}
            className="h-9 px-3 rounded-lg bg-[#D3D86F] hover:bg-[#c4ca5e] text-black text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-economy-grant">
            <Coins className="h-3.5 w-3.5" /> Grant Coins
          </button>
          <button onClick={setHouse} disabled={busy}
            className="h-9 px-3 rounded-lg bg-black hover:bg-gray-800 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="admin-economy-set-house">
            <ShieldAlert className="h-3.5 w-3.5" /> Set House
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Profiles"    value={overview.total_profiles}       testid="admin-econ-profiles" />
        <StatCard label="Coins Earned" value={overview.total_coins_earned}  testid="admin-econ-earned" tone="gold" />
        <StatCard label="Coins Spent"  value={overview.total_coins_spent}   testid="admin-econ-spent" />
        <StatCard label="In Circulation" value={overview.coins_in_circulation} testid="admin-econ-circulation" />
        <StatCard label="Purchases"   value={overview.total_purchases}       testid="admin-econ-purchases" />
        <StatCard label="Referrals"   value={overview.total_referrals}       testid="admin-econ-referrals" />
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-gray-600 font-bold mb-2">Houses</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-econ-houses">
          {(overview.houses || []).map((h, i) => (
            <div key={h.key} className="rounded-xl border border-gray-200 bg-white p-3" data-testid={`admin-econ-house-${h.key}`}>
              <div className="flex items-center gap-2">
                <span className="text-2xl">{h.emoji}</span>
                <div>
                  <div className="text-black font-bold text-sm">{h.name}</div>
                  <div className="text-gray-500 text-[10px]">Rank #{i + 1}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1 mt-2 text-[10px] text-gray-600">
                <div><b>{h.points}</b><div>points</div></div>
                <div><b>{h.xp}</b><div>XP</div></div>
                <div><b>{h.members}</b><div>members</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-gray-600 font-bold mb-2 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Top Earners
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="admin-econ-top-earners">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600 uppercase tracking-wider text-[10px]">
              <tr><th className="text-left px-3 py-2">Email</th><th className="text-right px-3 py-2">Balance</th><th className="text-right px-3 py-2">Earned</th><th className="text-right px-3 py-2">Spent</th></tr>
            </thead>
            <tbody>
              {(overview.top_earners || []).map((u) => (
                <tr key={u.email} className="border-t border-gray-100" data-testid={`admin-econ-earner-${u.email}`}>
                  <td className="px-3 py-2 font-mono text-[11px]">{u.email}</td>
                  <td className="px-3 py-2 text-right font-bold text-amber-700">{u.balance || 0}</td>
                  <td className="px-3 py-2 text-right">{u.lifetime_earned || 0}</td>
                  <td className="px-3 py-2 text-right">{u.lifetime_spent || 0}</td>
                </tr>
              ))}
              {(overview.top_earners || []).length === 0 && (
                <tr><td colSpan="4" className="px-3 py-6 text-center text-gray-400 italic">No earners yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
